import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useTheme } from '../contexts/ThemeContext';
import {
  Search,
  Button,
  InlineNotification,
  TextInput,
  ToastNotification,
  Modal,
  Tag,
  Select,
  SelectItem,
  MultiSelect,
  TextArea,
  SkeletonPlaceholder,
  SkeletonText,
  Tile
} from '@carbon/react';
import { Add, TrashCan, ChevronLeft, ChevronRight, Close, Edit, Run, Restaurant, Music, Activity, ArrowLeft, ArrowRight, Checkmark } from '@carbon/icons-react';
import axiosInstance from '../utils/axiosInstance';
import authService from '../services/authService';
import PageContainer from '../components/PageContainer';
import './Activities.css';

// Icon map keyed by ActivityType enum value — no icon field is sent to or stored by the API
const ACTIVITY_ICON_MAP = {
  Physical: Run,
  Nutrition: Restaurant,
  Entertainment: Music,
};
const DEFAULT_ACTIVITY_ICON = Activity;

function getActivityIcon(activityType) {
  return ACTIVITY_ICON_MAP[activityType] || DEFAULT_ACTIVITY_ICON;
}

const EMPTY_FORM = { name: '', duration: '', activityType: '', employeeTypeIds: [], description: '' };

const Activities = ({ embedded = false }) => {
  useTheme();
  const userRole = authService.getUserRole();
  const isAdmin = userRole === 'administrator';

  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(5);
  const [pagination, setPagination] = useState({
    totalCount: 0,
    totalPages: 1,
    hasNextPage: false,
    hasPreviousPage: false
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [pageFading, setPageFading] = useState(false);

  const [activityTypes, setActivityTypes] = useState([]);
  const [employeeTypes, setEmployeeTypes] = useState([]);

  const [isSidePanelOpen, setIsSidePanelOpen] = useState(false);
  const [editingActivity, setEditingActivity] = useState(null);
  const [viewingActivity, setViewingActivity] = useState(null);
  const [viewLoading, setViewLoading] = useState(false);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [formErrors, setFormErrors] = useState({});
  const [formError, setFormError] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [stepTransition, setStepTransition] = useState('entering');
  const stepTransitionTimeout = useRef(null);

  const steps = [
    { label: 'Osnovni podaci', description: 'Naziv, trajanje i tip' },
    { label: 'Zaposleni', description: 'Ko prati aktivnost' },
    { label: 'Opis', description: 'Dodatne informacije' },
  ];

  const [toast, setToast] = useState({ show: false, kind: 'success', title: '', subtitle: '' });
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [activityToDelete, setActivityToDelete] = useState(null);

  // Fetch activities (with paging)
  const fetchActivities = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams();
      if (searchTerm) {
        params.append('search', searchTerm);
      }

      const activitiesRes = await axiosInstance.get(`/api/Activity?${params.toString()}`);

      // Normalize activities envelope
      let data = [];
      if (Array.isArray(activitiesRes.data)) {
        data = activitiesRes.data;
      } else if (activitiesRes.data?.data && Array.isArray(activitiesRes.data.data)) {
        data = activitiesRes.data.data;
      }
      setActivities(data);

      // Client-side pagination — the backend /api/Activity endpoint does not
      // honor pageNumber/pageSize (it always returns its own default page),
      // so we fetch everything and paginate locally with pageSize = 5.
      const totalCount = data.length;
      const totalPages = Math.ceil(totalCount / pageSize) || 1;
      setPagination({
        totalCount,
        totalPages,
        hasNextPage: page < totalPages,
        hasPreviousPage: page > 1
      });
    } catch (err) {
      console.error('Greška pri učitavanju aktivnosti:', err);
      setError('Nije moguće učitati aktivnosti. Pokušajte ponovo.');
      setActivities([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch activity types and employee types (once, no paging)
  const fetchTypes = async () => {
    try {
      const typesRes = await axiosInstance.get('/api/Activity/activity-types');

      // Normalize activity types envelope
      let types = [];
      if (typesRes.data?.data?.activityTypes) {
        types = typesRes.data.data.activityTypes;
      } else if (Array.isArray(typesRes.data?.activityTypes)) {
        types = typesRes.data.activityTypes;
      } else if (Array.isArray(typesRes.data?.data)) {
        types = typesRes.data.data;
      } else if (Array.isArray(typesRes.data)) {
        types = typesRes.data;
      }
      setActivityTypes(types);
    } catch (err) {
      console.error('Greška pri učitavanju tipova aktivnosti:', err);
      setActivityTypes([]);
    }

    try {
      const employeeTypesRes = await axiosInstance.get('/api/EmployeeType');

      // Normalize employee types envelope
      let empTypes = [];
      if (Array.isArray(employeeTypesRes.data)) {
        empTypes = employeeTypesRes.data;
      } else if (employeeTypesRes.data?.data && Array.isArray(employeeTypesRes.data.data)) {
        empTypes = employeeTypesRes.data.data;
      }
      setEmployeeTypes(empTypes);
    } catch (err) {
      console.error('Greška pri učitavanju tipova zaposlenih:', err);
      setEmployeeTypes([]);
    }
  };

  useEffect(() => {
    fetchActivities();
  }, [page, searchTerm]);

  useEffect(() => {
    fetchTypes();
  }, []);

  const showToast = (kind, title, subtitle, duration = 3000) => {
    setToast({ show: true, kind, title, subtitle });
    setTimeout(() => setToast(prev => ({ ...prev, show: false })), duration);
  };

  // --- Delete ---
  const handleDeleteActivity = (activity, e) => {
    e.stopPropagation();
    setActivityToDelete(activity);
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!activityToDelete) return;
    try {
      await axiosInstance.delete(`/api/Activity/${activityToDelete.id}`);
      showToast('success', 'Uspešno!', `Aktivnost "${activityToDelete.name}" je uspešno obrisana.`);
      fetchActivities();
    } catch (err) {
      console.error('Greška pri brisanju aktivnosti:', err);
      showToast('error', 'Greška!', 'Nije moguće obrisati aktivnost. Pokušajte ponovo.', 4000);
    } finally {
      setIsDeleteModalOpen(false);
      setActivityToDelete(null);
    }
  };

  // --- Pagination ---
  const nextPage = () => {
    if (pagination.hasNextPage && page < pagination.totalPages) {
      setPageFading(true);
      setTimeout(() => { setPage(prev => Math.min(prev + 1, pagination.totalPages)); setPageFading(false); }, 300);
    }
  };

  const prevPage = () => {
    if (pagination.hasPreviousPage && page > 1) {
      setPageFading(true);
      setTimeout(() => { setPage(prev => Math.max(prev - 1, 1)); setPageFading(false); }, 300);
    }
  };

  // --- Detail view ---
  const openDetailPanel = async (activity, e) => {
    if (e) e.stopPropagation();
    setViewingActivity(null);
    setViewLoading(true);
    setEditingActivity(null);
    setFormError(null);
    setIsSidePanelOpen(true);
    try {
      const response = await axiosInstance.get(`/api/Activity/${activity.id}`);
      const detail = response.data?.data || response.data;
      setViewingActivity(detail);
    } catch (err) {
      console.error('Greška pri učitavanju detalja aktivnosti:', err);
      setViewingActivity(activity);
      setFormError('Nije moguće učitati detalje aktivnosti. Pokušajte ponovo.');
    } finally {
      setViewLoading(false);
    }
  };

  const closeSidePanel = () => {
    setIsSidePanelOpen(false);
    setViewingActivity(null);
    setEditingActivity(null);
    setFormError(null);
    setCurrentStep(0);
    setStepTransition('entering');
  };

  // --- Form ---
  const openCreatePanel = () => {
    setEditingActivity(null);
    setViewingActivity(null);
    setFormData(EMPTY_FORM);
    setFormErrors({});
    setFormError(null);
    setCurrentStep(0);
    setStepTransition('entering');
    setIsSidePanelOpen(true);
  };

  const openEditPanel = (activity, e) => {
    e.stopPropagation();
    setEditingActivity(activity);
    setViewingActivity(null);
    setFormData({
      name: activity.name,
      duration: String(activity.duration),
      activityType: activity.activityType,
      employeeTypeIds: Array.isArray(activity.employeeTypeIds) ? activity.employeeTypeIds : [],
      description: activity.description || '',
    });
    setFormErrors({});
    setFormError(null);
    setCurrentStep(0);
    setStepTransition('entering');
    setIsSidePanelOpen(true);
  };

  const goToStep = (step) => {
    setStepTransition('exiting');
    setTimeout(() => {
      setCurrentStep(step);
      setStepTransition('entering');
    }, 150);
  };

  const nextStep = () => {
    if (currentStep < steps.length - 1) {
      goToStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      goToStep(currentStep - 1);
    }
  };

  const handleStepClick = (targetStep) => {
    // Dozvoli vraćanje unazad
    if (targetStep < currentStep) {
      goToStep(targetStep);
      return;
    }

    // Ako pokušava da ide na trenutni korak, ne radi ništa
    if (targetStep === currentStep) {
      return;
    }

    // Proveri da li može da ide napred
    if (targetStep > currentStep) {
      // Ne dozvoli preskakanje koraka
      if (targetStep > currentStep + 1) {
        return;
      }
      // Ne dozvoli prelazak ako trenutni korak nije validan
      if (!isStepValid()) {
        return;
      }
      goToStep(targetStep);
    }
  };

  const isStepValid = () => {
    switch (currentStep) {
      case 0: // Osnovni podaci
        return formData.name.trim() && formData.duration && formData.activityType;
      case 1: // Zaposleni
        return Array.isArray(formData.employeeTypeIds) && formData.employeeTypeIds.length > 0;
      default:
        return true; // Opis je opcioni
    }
  };

  const handleFormChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.name.trim()) {
      errors.name = 'Naziv je obavezan.';
    } else if (formData.name.trim().length > 200) {
      errors.name = 'Naziv ne može biti duži od 200 karaktera.';
    }
    const dur = parseInt(formData.duration, 10);
    if (!formData.duration) {
      errors.duration = 'Trajanje je obavezno.';
    } else if (isNaN(dur) || dur < 1 || dur > 1440) {
      errors.duration = 'Trajanje mora biti između 1 i 1440 minuta.';
    }
    if (!formData.activityType) {
      errors.activityType = 'Tip aktivnosti je obavezan.';
    }
    if (!Array.isArray(formData.employeeTypeIds) || formData.employeeTypeIds.length === 0) {
      errors.employeeTypeIds = 'Potreban je najmanje jedan tip zaposlenog.';
    }
    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    setFormLoading(true);
    setFormError(null);

    const payload = {
      name: formData.name.trim(),
      duration: parseInt(formData.duration, 10),
      activityType: formData.activityType,
      employeeTypeIds: formData.employeeTypeIds,
      description: formData.description?.trim() || '',
    };

    try {
      if (editingActivity) {
        await axiosInstance.put(`/api/Activity/${editingActivity.id}`, payload);
        setIsSidePanelOpen(false);
        showToast('success', 'Uspešno!', `Aktivnost "${payload.name}" je uspešno izmenjena.`);
      } else {
        await axiosInstance.post('/api/Activity', payload);
        setIsSidePanelOpen(false);
        showToast('success', 'Uspešno!', `Aktivnost "${payload.name}" je uspešno dodata.`);
      }
      fetchActivities();
    } catch (err) {
      console.error('Greška pri čuvanju aktivnosti:', err);
      const msg = err.response?.data?.message || err.response?.data?.errors?.[0] || 'Nije moguće sačuvati aktivnost. Pokušajte ponovo.';
      setFormError(msg);
      showToast('error', 'Greška!', msg, 4000);
    } finally {
      setFormLoading(false);
    }
  };

  const getTypeDisplayName = (typeValue) => {
    const found = activityTypes.find(t => t.value === typeValue);
    return found ? found.displayName : typeValue;
  };

  // Resolve display names for an activity's employee types.
  // Prefers the API-provided employeeTypeNames (index-aligned with employeeTypeIds);
  // falls back to the locally loaded employeeTypes list.
  const getEmployeeTypeNames = (activity) => {
    if (!activity) return [];
    if (Array.isArray(activity.employeeTypeNames) && activity.employeeTypeNames.length > 0) {
      return activity.employeeTypeNames;
    }
    if (Array.isArray(activity.employeeTypeIds)) {
      return activity.employeeTypeIds
        .map(id => employeeTypes.find(t => t.id === id)?.name)
        .filter(Boolean);
    }
    return [];
  };

  // --- Loading state ---
  if (loading) {
    return (
      <PageContainer bare={embedded}>
        <div className="activities-container">
          <div className="activities-toolbar">
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flex: 1 }}>
              <SkeletonText style={{ width: '100%', maxWidth: '500px', height: '40px' }} />
            </div>
            <SkeletonText style={{ width: '160px', height: '40px' }} />
          </div>
          <div className="activities-grid activities-grid--skeleton">
            {[...Array(6)].map((_, i) => (
              <SkeletonPlaceholder key={i} className="activity-card-skeleton" />
            ))}
          </div>
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer bare={embedded}>
      {/* Error notification */}
      {error && (
        <div style={{
          position: 'fixed', top: '4rem', right: '1rem', zIndex: 9999,
          minWidth: '320px', animation: 'slideInFromRight 0.4s cubic-bezier(0.4, 0, 0.2, 1)'
        }}>
          <style>{`
            @keyframes slideInFromRight {
              from { transform: translateX(calc(100% + 1rem)); opacity: 0; }
              to { transform: translateX(0); opacity: 1; }
            }
          `}</style>
          <InlineNotification
            kind="error"
            title="Greška"
            subtitle={error}
            onCloseButtonClick={() => setError(null)}
            hideCloseButton={false}
            lowContrast
            actions={
              <Button
                kind="ghost"
                size="sm"
                onClick={() => { setError(null); window.location.reload(); }}
              >
                Pokušaj ponovo
              </Button>
            }
          />
        </div>
      )}

      <div className="activities-container">
        {/* Toolbar */}
        <div className="activities-toolbar">
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flex: 1 }}>
            <Search
              size="lg"
              placeholder="Pretražite aktivnosti..."
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); setPage(1); }}
              onClear={() => { setSearchTerm(''); setPage(1); }}
              labelText=""
              closeButtonLabelText="Obriši pretragu"
              style={{ flex: 1 }}
            />
            {pagination.totalCount > pageSize && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Button
                  kind="ghost"
                  size="md"
                  renderIcon={ChevronLeft}
                  onClick={prevPage}
                  hasIconOnly
                  disabled={!pagination.hasPreviousPage}
                  iconDescription="Prethodna strana"
                />
                <span style={{ fontSize: '0.875rem', opacity: 0.8, minWidth: '60px', textAlign: 'center', fontWeight: '500' }}>
                  {page}/{pagination.totalPages}
                </span>
                <Button
                  kind="ghost"
                  size="md"
                  renderIcon={ChevronRight}
                  onClick={nextPage}
                  hasIconOnly
                  disabled={!pagination.hasNextPage}
                  iconDescription="Sledeća strana"
                />
              </div>
            )}
          </div>
          {isAdmin && (
            <Button
              kind="primary"
              renderIcon={Add}
              onClick={openCreatePanel}
              className="activities-add-button"
            >
              Dodaj aktivnost
            </Button>
          )}
        </div>

        {/* Content */}
        {pagination.totalCount === 0 ? (
          <div className="activities-empty">
            <Activity size={64} className="empty-icon" />
            <p className="empty-title">Nema aktivnosti</p>
            <p className="empty-subtitle">
              {searchTerm
                ? 'Pokušajte sa drugom pretragom'
                : 'Dodajte prvu aktivnost'}
            </p>
            {isAdmin && !searchTerm && (
              <Button kind="primary" renderIcon={Add} onClick={openCreatePanel} style={{ marginTop: '1rem' }}>
                Dodaj aktivnost
              </Button>
            )}
          </div>
        ) : (
          <div
            className="activities-grid"
            style={{
              opacity: pageFading ? 0 : 1,
              transition: 'opacity 0.3s ease',
              animation: 'fadeIn 0.4s ease-in'
            }}
          >
            {activities
              .slice((page - 1) * pageSize, page * pageSize)
              .map((activity) => {
                const IconComponent = getActivityIcon(activity.activityType);
                return (
                  <div
                    key={activity.id}
                    className="activity-card"
                    onClick={(e) => openDetailPanel(activity, e)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        openDetailPanel(activity, e);
                      }
                    }}
                  >
                    {/* Icon area */}
                    <div className="activity-card-icon-area">
                      <IconComponent size={48} className="activity-card-icon" />
                    </div>

                    {/* Card body */}
                    <div className="activity-card-body">
                      <h3 className="activity-card-name">{activity.name}</h3>
                      <div className="activity-card-meta">
                        <Tag type="teal" size="sm">
                          {getTypeDisplayName(activity.activityType)}
                        </Tag>
                      </div>
                    </div>
                  </div>
                );
              })}
          </div>
        )}

        {/* Right side panel */}
        <div className={`activity-side-panel ${isSidePanelOpen ? 'open' : ''}`}>
          <div className="side-panel-header">
            <h2>
              {viewingActivity
                ? 'Detalji aktivnosti'
                : editingActivity
                  ? 'Izmeni aktivnost'
                  : 'Dodaj aktivnost'}
            </h2>
            <Button
              kind="ghost"
              size="sm"
              renderIcon={Close}
              hasIconOnly
              onClick={closeSidePanel}
              iconDescription="Zatvori"
            />
          </div>

          {formError && (
            <div style={{ padding: '1rem 1.5rem 0' }}>
              <InlineNotification
                kind="error"
                title="Greška"
                subtitle={formError}
                onCloseButtonClick={() => setFormError(null)}
                hideCloseButton={false}
                lowContrast
              />
            </div>
          )}

          {viewingActivity && (
            <div className="side-panel-content">
              <div className="activity-detail">
                <div className="activity-detail-icon">
                  {(() => {
                    const IconComponent = getActivityIcon(viewingActivity.activityType);
                    return <IconComponent size={48} />;
                  })()}
                </div>
                <h3 className="activity-detail-name">{viewingActivity.name}</h3>
                <div className="activity-detail-meta">
                  <Tag type="teal" size="sm">
                    {getTypeDisplayName(viewingActivity.activityType)}
                  </Tag>
                  <span className="activity-detail-duration">
                    Trajanje: {viewingActivity.duration} min
                  </span>
                </div>
                {getEmployeeTypeNames(viewingActivity).length > 0 && (
                  <div className="activity-detail-section">
                    <h4 className="activity-detail-label">Zaposleni koji prate aktivnost</h4>
                    <p className="activity-detail-description">
                      {getEmployeeTypeNames(viewingActivity).join(', ')}
                    </p>
                  </div>
                )}
                {viewingActivity.description && (
                  <div className="activity-detail-section">
                    <h4 className="activity-detail-label">Opis</h4>
                    <p className="activity-detail-description">{viewingActivity.description}</p>
                  </div>
                )}
                {isAdmin && (
                  <div className="activity-detail-actions">
                    <Button
                      kind="primary"
                      renderIcon={Edit}
                      onClick={(e) => openEditPanel(viewingActivity, e)}
                    >
                      Izmeni
                    </Button>
                    <Button
                      kind="danger"
                      renderIcon={TrashCan}
                      onClick={(e) => handleDeleteActivity(viewingActivity, e)}
                    >
                      Obriši
                    </Button>
                  </div>
                )}
              </div>
            </div>
          )}

          {viewLoading && (
            <div className="side-panel-content">
              <div className="activity-detail-loading">
                <SkeletonText style={{ width: '60%', height: '28px' }} />
                <SkeletonText style={{ width: '40%', height: '20px' }} />
                <SkeletonText style={{ width: '100%', height: '80px' }} />
              </div>
            </div>
          )}

          {!viewingActivity && !viewLoading && (
            <div className="side-panel-content">
              <form onSubmit={handleSubmit} className="activity-form">
                {/* Custom progress bar */}
                <div className="activity-progress-bar">
                  <div className="progress-steps">
                    {steps.map((step, index) => (
                      <React.Fragment key={index}>
                        {index > 0 && (
                          <div className={`progress-line ${currentStep >= index ? 'completed' : ''}`}></div>
                        )}
                        <div
                          className={`progress-step ${currentStep >= index ? 'completed' : ''} ${currentStep === index ? 'active' : ''}`}
                          onClick={() => handleStepClick(index)}
                          style={{ cursor: 'pointer' }}
                        >
                          <div className="step-circle">
                            {currentStep > index ? <Checkmark size={16} /> : index + 1}
                          </div>
                          <div className="step-label">
                            <div className="step-title">{step.label}</div>
                            {currentStep > index && (
                              <div className="step-description">{step.description}</div>
                            )}
                          </div>
                        </div>
                      </React.Fragment>
                    ))}
                  </div>
                </div>

                <div className={`step-content ${stepTransition}`}>
                  <Tile style={{ padding: '1.25rem', marginBottom: '1.5rem' }}>
                    <h3 style={{ margin: '0 0 1rem 0', fontSize: '1rem', fontWeight: '500' }}>
                      {steps[currentStep].label}
                    </h3>

                    {currentStep === 0 && (
                      <>
                        <TextInput
                          id="activity-name"
                          labelText="Naziv aktivnosti"
                          placeholder="Unesite naziv aktivnosti"
                          value={formData.name}
                          onChange={(e) => handleFormChange('name', e.target.value)}
                          invalid={!!formErrors.name}
                          invalidText={formErrors.name}
                          disabled={formLoading}
                        />

                        <TextInput
                          id="activity-duration"
                          labelText="Trajanje (minuti)"
                          placeholder="npr. 30"
                          value={formData.duration}
                          onChange={(e) => handleFormChange('duration', e.target.value)}
                          invalid={!!formErrors.duration}
                          invalidText={formErrors.duration}
                          disabled={formLoading}
                          type="number"
                          min="1"
                          max="1440"
                          step="1"
                        />

                        <Select
                          id="activity-type"
                          labelText="Tip aktivnosti"
                          value={formData.activityType}
                          onChange={(e) => handleFormChange('activityType', e.target.value)}
                          invalid={!!formErrors.activityType}
                          invalidText={formErrors.activityType}
                          disabled={formLoading || activityTypes.length === 0}
                        >
                          <SelectItem value="" text="Izaberite tip aktivnosti" />
                          {activityTypes.map((type) => (
                            <SelectItem key={type.value} value={type.value} text={type.displayName} />
                          ))}
                        </Select>
                      </>
                    )}

                    {currentStep === 1 && (
                      <MultiSelect
                        id="activity-employee-types"
                        titleText="Zaposleni koji prate aktivnost"
                        label="Izaberite jedan ili više tipova zaposlenih"
                        items={employeeTypes}
                        itemToString={(item) => (item ? item.name : '')}
                        selectedItems={employeeTypes.filter(t => formData.employeeTypeIds.includes(t.id))}
                        onChange={({ selectedItems }) =>
                          handleFormChange('employeeTypeIds', (selectedItems || []).map(t => t.id))
                        }
                        invalid={!!formErrors.employeeTypeIds}
                        invalidText={formErrors.employeeTypeIds}
                        disabled={formLoading || employeeTypes.length === 0}
                        selectionFeedback="top-after-reopen"
                      />
                    )}

                    {currentStep === 2 && (
                      <TextArea
                        id="activity-description"
                        labelText="Opis (opciono)"
                        placeholder="Unesite opis aktivnosti"
                        value={formData.description}
                        onChange={(e) => handleFormChange('description', e.target.value)}
                        disabled={formLoading}
                        rows={4}
                        maxLength={1000}
                        helperText="Kratak opis aktivnosti (do 1000 karaktera)"
                      />
                    )}
                  </Tile>
                </div>

                {/* Step navigation */}
                <div className="form-actions">
                  <Button
                    kind="secondary"
                    onClick={prevStep}
                    disabled={formLoading || currentStep === 0}
                    type="button"
                    renderIcon={ArrowLeft}
                  >
                    Nazad
                  </Button>
                  {currentStep < steps.length - 1 ? (
                    <Button
                      kind="primary"
                      type="button"
                      renderIcon={ArrowRight}
                      onClick={nextStep}
                      disabled={!isStepValid()}
                    >
                      Sledeći korak
                    </Button>
                  ) : (
                    <Button
                      kind="primary"
                      type="submit"
                      renderIcon={Checkmark}
                      disabled={formLoading}
                    >
                      {formLoading ? 'Čuvanje...' : 'Sačuvaj'}
                    </Button>
                  )}
                </div>
              </form>
            </div>
          )}
        </div>

        {/* Side panel overlay */}
        {isSidePanelOpen && (
          <div className="side-panel-overlay" onClick={closeSidePanel} />
        )}

        {/* Toast notification */}
        {toast.show && (
          <div className="toast-container">
            <ToastNotification
              kind={toast.kind}
              title={toast.title}
              subtitle={toast.subtitle}
              timeout={0}
              onClose={() => setToast(prev => ({ ...prev, show: false }))}
              lowContrast
            />
          </div>
        )}

        {/* Delete confirmation modal */}
        <Modal
          open={isDeleteModalOpen}
          onRequestClose={() => setIsDeleteModalOpen(false)}
          modalHeading="Brisanje aktivnosti"
          primaryButtonText="Obriši"
          secondaryButtonText="Otkaži"
          onRequestSubmit={confirmDelete}
          danger
          size="sm"
        >
          <p>
            Da li ste sigurni da želite da obrišete aktivnost{' '}
            <strong>{activityToDelete?.name}</strong>?
          </p>
          <p style={{ marginTop: '1rem', fontSize: '0.875rem', opacity: 0.7 }}>
            Ova akcija se ne može poništiti.
          </p>
        </Modal>

        {/* Floating Action Button (mobile only) */}
        {isAdmin && !isSidePanelOpen && ReactDOM.createPortal(
          <button
            className="floating-button activities-fab"
            onClick={openCreatePanel}
            style={{
              position: 'fixed',
              bottom: '2rem',
              right: '2rem',
              height: '56px',
              padding: '0 24px',
              backgroundColor: '#9fb746',
              border: 'none',
              borderRadius: '28px',
              display: 'none',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              boxShadow: 'none',
              transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
              gap: '12px',
              outline: 'none',
              zIndex: 9999
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#8da63d';
              e.currentTarget.style.transform = 'translateY(-2px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#9fb746';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
            onMouseDown={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px) scale(0.97)';
            }}
            onMouseUp={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px) scale(1)';
            }}
          >
            <Add
              size={24}
              style={{
                flexShrink: 0,
                minWidth: '24px',
                minHeight: '24px',
                fill: '#ffffff'
              }}
              aria-label="Dodaj aktivnost"
              color="#ffffff"
            />
            <span style={{
              color: 'white',
              fontWeight: '500',
              fontSize: '0.95rem',
              whiteSpace: 'nowrap',
              userSelect: 'none'
            }}>
              Dodaj aktivnost
            </span>
          </button>,
          document.body
        )}
      </div>
    </PageContainer>
  );
};

export default Activities;
